import CalendarGroup from 'components/elements/form/CalendarGroup'
import Text from 'components/elements/texts/Text'
import RequestTemplate from 'components/templates/RequestTemplate'
import { DEFAULT_DISABLED_BUTTON_LABEL } from 'constants/label'
import { PAGE_PATH } from 'constants/pagePath'
import {
  RESERVATION_DATE_FORMAT,
  RESERVATION_DATE_MAX,
} from 'constants/qnState'
import dayjs from 'dayjs'
import React, { useEffect, useCallback, useMemo, useContext } from 'react'
import { QnStatesProps } from 'states/QnState'
import styled from 'styled-components'
import { StatesProps } from 'utils/commonHooks'
import { EditingItemContext } from 'contexts/EditingItemContext'
import TimePicker from 'components/elements/items/TimePicker'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import { success } from 'utils/status'
import { initialQnEditState } from '../../states/QnState'

dayjs.extend(advancedFormat)

/**
 * QN-08-a: 予約画面
 */
const Reservation: React.FC<StatesProps> = (props: QnStatesProps) => {
  const { state, setState } = props

  /** 現在日付/時間 */
  const now = dayjs()
  /** 終了時間を計算する際に追加する時間 */
  const endTimeHour = 2
  /** stateの管理 */
  const [editingItem, setEditingItem] = useContext(EditingItemContext)

  const [minDate, minTime, maxDate] = useMemo(() => {
    let minDate = now
    const hour = now.hour()
    const minute = now.minute()
    // 現時刻が、16:01〜23:59 または 0:00〜6:00 に該当するか判断
    const overtimeReservation =
      hour > 16 ||
      (hour === 16 && minute >= 1) ||
      hour < 6 ||
      (hour === 6 && minute === 0)
    if (overtimeReservation) {
      // 現時刻が、0:00 ~ 6:00 に該当する場合は、当日の8:00に設定
      if (hour < 6 || (hour === 6 && minute === 0)) {
        minDate = minDate.set('hour', 8).set('minute', 0)
      } else {
        // 現時刻が 16:01 ~ 23:59 に該当する場合は、翌日の8:00に設定
        minDate = minDate.add(1, 'day').set('hour', 8).set('minute', 0)
      }
    } else {
      // 通常の予約可能時間帯 ／ 06:01 ~ 16:00 に該当する場合
      const intervalHour = now.get('minute') <= 30 ? 2 : 3
      minDate = minDate.add(intervalHour, 'hour')
      const minDateMinute = now.get('minute')
      // 1分～30分 -> 30分に設定 それ以外は 0分 に設定
      1 <= minDateMinute && minDateMinute <= 30
        ? (minDate = minDate.set('minute', 30))
        : (minDate = minDate.set('minute', 0))
    }
    return [
      minDate,
      minDate.format('HH:mm'),
      minDate.add(RESERVATION_DATE_MAX, 'day'),
    ]
  }, [now])

  /** 選択中の予約日 */
  const selectedDate: dayjs.Dayjs = useMemo(() => {
    return dayjs(editingItem.reservation.date || undefined)
  }, [editingItem.reservation.date])

  /** 予約最小時間 */
  const selectedMinTime: string | undefined = useMemo(() => {
    return selectedDate.isSame(minDate, 'day') ? minTime : undefined
  }, [selectedDate, minDate, minTime])

  /** 予約終了時刻 */
  const endTime = useMemo(() => {
    return dayjs(`2000/1/1 ${editingItem.reservation.time}`)
      .add(endTimeHour, 'h')
      .format('HH:mm')
  }, [editingItem.reservation.time])

  /** エラーメッセージ */
  const errorMessage = useMemo(() => {
    return selectedDate && editingItem.reservation.time
      ? ''
      : DEFAULT_DISABLED_BUTTON_LABEL
  }, [selectedDate, editingItem.reservation.time])

  /** カレンダーの日付を切り替えたときのイベント */
  const handleCalendarChanged = useCallback(
    (date: dayjs.Dayjs): void => {
      /** 入力後の予約日/予約時間 */
      const updatedReservation = {
        ...editingItem,
        reservation: {
          ...editingItem.reservation,
          date: date.format(RESERVATION_DATE_FORMAT),
        },
      }
      setEditingItem({
        ...updatedReservation,
      })
    },
    [editingItem, setEditingItem]
  )

  /** 予約時間を切り替えたときのイベント */
  const handleTimeChanged = useCallback(
    (time: string): void => {
      setEditingItem({
        ...editingItem,
        reservation: {
          ...editingItem.reservation,
          time,
        },
      })
    },
    [setEditingItem]
  )

  /** 次の画面に遷移先 */
  const forwardTo = useMemo(() => {
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : PAGE_PATH.TROUBLE_PLACE
  }, [])

  /** 前の画面の戻り先 */
  const backTo = useMemo(() => {
    return state.confirmationChangeType
      ? PAGE_PATH.CONFIRMATION
      : PAGE_PATH.START
  }, [state.confirmationChangeType])

  /** プログレストラッカーの点灯位置 */
  const progress = useMemo(() => {
    // 確認画面から予約画面に遷移した場合は5番目、それ以外は1番目のポイントを点灯する
    return state.confirmationChangeType ? 5 : 1
  }, [])

  const forwardAction = useCallback(() => {
    setState({
      ...state,
      reservation: editingItem.reservation,
    })
    return success()
  }, [editingItem.reservation])

  /** 戻るボタン押下時にeditingItemの中身をqnStateの内容と同期させる */
  const backAction = useCallback(() => {
    if (state.confirmationChangeType) {
      setEditingItem({
        ...editingItem,
        reservation: state.reservation,
      })
    } else {
      setState({
        ...state,
        reservation: initialQnEditState.reservation,
      })
      setEditingItem({
        ...editingItem,
        reservation: initialQnEditState.reservation,
      })
    }
    return success()
  }, [state.confirmationChangeType])

  useEffect(() => {
    // 日付/時間の初期化
    // minDate の場合 -> 最短日時 / 最短時間に設定
    // minDate ではない場合 -> 日時や時間を保持
    // 当日の時間が16:01以降の場合、現在日付に1日加算し08:00にする
    const judgementDate = minDate > selectedDate
    editingItem.reservation.date = judgementDate
      ? minDate.format(RESERVATION_DATE_FORMAT)
      : selectedDate.format(RESERVATION_DATE_FORMAT)
    editingItem.reservation.time = judgementDate
      ? minTime
      : dayjs(`2000/01/01 ${editingItem.reservation.time}`).format('HH:mm')

    setEditingItem({
      ...editingItem,
      reservation: {
        date: editingItem.reservation.date,
        time: editingItem.reservation.time,
      },
    })
  }, [])

  return (
    <RequestTemplate
      title={'日時を指定して呼ぶ'}
      showContact
      progress={progress}
      content={
        <>
          <CalendarGroup
            title=""
            selectedDate={selectedDate}
            formName={'calendar'}
            minDate={minDate}
            maxDate={maxDate}
            onChanged={handleCalendarChanged}
          />
          <WhiteBox>
            <TimeMessage>
              左側の時計をタップして
              <br />
              ご希望の時間帯を選択してください。
            </TimeMessage>
            <InputTimeArea>
              <TimePicker
                time={editingItem.reservation.time}
                minTime={selectedMinTime}
                onChanged={handleTimeChanged}
              />
              <Range>～</Range>
              <EndTimeArea>{endTime}</EndTimeArea>
            </InputTimeArea>
            <DescriptionArea>
              <NoteText>日時指定の場合、2時間の幅を頂いております。</NoteText>
              <NoteText>
                状況によってはご希望に沿えない場合がございます。
              </NoteText>
              <NoteText>
                詳細な到着時間は、対応スタッフよりご連絡いたします。
              </NoteText>
            </DescriptionArea>
          </WhiteBox>
        </>
      }
      error={errorMessage}
      forwardAction={forwardAction}
      forwardTo={forwardTo}
      backAction={backAction}
      backTo={backTo}
      isDisabled={errorMessage.length > 0}
      showBack
      notClearEditingItem
    />
  )
}

/** 注釈文のスタイル */
const NoteText = styled(Text)`
  margin: 8px 16px 0;
  padding-left: 1em;
  color: #ff0000;
  font-weight: bold;
  font-size: 13px;
  text-indent: -1.6em;
  &::before {
    margin-right: 8px;
    content: '※';
  }
`

/** 背景色が白のボックスのスタイル */
const WhiteBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 8px 0px;
  padding: 8px;
  background-color: #ffffff;
`

/** 予約時間に関する説明文のスタイル定義 */
const TimeMessage = styled.div`
  font-weight: bold;
  text-align: center;
`

/** 予約時間を入力するエリアのスタイル定義 */
const InputTimeArea = styled.div`
  display: flex;
  align-items: center;
`

/** 予約時間の間(～)のスタイル定義 */
const Range = styled.div`
  margin: 8px;
  font-size: 36px;
  content: '～';
`

/** 予約終了時間のスタイル定義 */
const EndTimeArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 114px;
  height: 53px;
  padding: 4px 8px;
  font-weight: bold;
  font-size: 18px;
  background-color: #999999;
  border: 1px solid var(--color-text-for-main-background);
`
const DescriptionArea = styled.div`
  background-color: #ffffffff;
`

export default Reservation
